'use client'

import Link from "next/link"
import Image, { StaticImageData } from "next/image"
import { useTranslations } from 'next-intl'
import { useRTLAwareStyle } from '@/util/rtl'
import { motion } from 'framer-motion'
import { useInView } from '@/hooks/useInView'

type Props = {
    title: string;
    desc: string;
    img: string | StaticImageData;
    alt: string;
    href: string;
}

export default function InfoCard({title, desc, img, alt, href}: Props) {
    const rtlAwareUnderlineStyle = useRTLAwareStyle('left-0', 'right-0');
    const letterSpacing = useRTLAwareStyle('tracking-wider', '')

    const t = useTranslations('home');
    const { ref, isInView } = useInView(0.2)
   
    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
    }
    
    const contentVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { 
                staggerChildren: 0.2,
                delayChildren: 0.2,
                ease: 'easeOut'
            } 
        }
    }
    
    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
    }

    return (
        <motion.div
            ref={ref}
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="bg-white overflow-hidden md:transition-shadow duration-300 hover:shadow-lg"
        >
            <Link href={href} className="block" prefetch={false}>
                <div className="relative w-full h-[400px] overflow-hidden ">
                    <Image
                        src={img}
                        alt={alt}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw"
                        className=" object-contain transition-transform duration-300 hover:scale-105"
                    />
                </div>
            </Link>
            <motion.div
                variants={contentVariants}
                className="p-6"
            >
                <motion.h3
                    variants={itemVariants}
                    className={`text-xl font-semibold mb-2 ${letterSpacing}`}
                >
                    {title}
                </motion.h3>
                <motion.p 
                    variants={itemVariants}
                    className='mb-4 text-sm text-gray-600'
                >
                    {desc}
                </motion.p>
                <Link
                    href={href}
                    className="inline-block relative group"
                    prefetch={false}
                >
                    <span className="text-[#bcb398] relative z-10 transition-colors duration-300 ease-in-out group-hover:text-gray-800 font-medium">
                        {t('link')}
                    </span>
                    <span className={`absolute bottom-0 ${rtlAwareUnderlineStyle} w-0 h-[1px] bg-gray-800 transition-all duration-300 ease-in-out group-hover:w-full`}></span>
                </Link>
            </motion.div>
        </motion.div>
    )
}